type ChatEvent = {
  organizationId: string;
  threadId: string;
  turnId: string;
  eventId: string;
  sequence: number;
  eventType: string;
  payload: string;
  createdAt: string;
};

export type TurnSummary = {
  turnId: string;
  createdAt: string;
  userMessage: {
    id: string;
    text: string;
    createdAt: string;
  } | null;
  assistantMessage: {
    id: string;
    text: string;
    createdAt: string;
  } | null;
  activityEvents: ChatEvent[];
  isLegacyFake: boolean;
};

export type ThinkingBlock = {
  id: string;
  text: string;
};

export type ToolTrace = {
  id: string;
  title: string;
  inputDetails: string | null;
  outputDetails: string | null;
  state: "working" | "complete" | "error";
  artifact: TurnArtifact | null;
};

export type ArtifactDownload = {
  format: "csv" | "parquet";
  file_name: string;
  s3_uri: string;
  content_type?: string;
};

export type ChartArtifact = {
  type: "chart";
  artifact_id?: string;
  title: string;
  description?: string;
  chart_type: "bar" | "line" | "area" | "scatter";
  dataset_name: string;
  folder: "inputs" | "outputs";
  x_key: string;
  y_key: string;
  series_key?: string | null;
  data: Array<Record<string, unknown>>;
  columns?: string[];
  rows_total_count?: number;
  downloads?: ArtifactDownload[];
};

export type CsvArtifact = {
  type: "csv";
  title: string;
  description?: string;
  file_name: string;
  dataset_name: string;
  folder: "inputs" | "outputs" | "settings";
  columns: string[];
  rows: Array<Record<string, unknown>>;
  rows_preview_count?: number;
  rows_total_count?: number;
};

export type TableArtifact = {
  type: "table";
  artifact_id: string;
  title: string;
  description?: string;
  dataset_name: string;
  folder: "inputs" | "outputs" | "settings";
  columns: string[];
  rows: Array<Record<string, unknown>>;
  rows_preview_count?: number;
  rows_total_count?: number;
  downloads?: ArtifactDownload[];
};

export type TurnArtifact = ChartArtifact | CsvArtifact | TableArtifact;

export type TurnViewModel = {
  turn: TurnSummary;
  thinkingBlocks: ThinkingBlock[];
  toolTraces: ToolTrace[];
  isRunning: boolean;
  isStale: boolean;
  hasError: boolean;
  statusLabel: string;
  statusBadgeLabel: string;
  statusTone: "working" | "complete" | "error" | "info";
  toolCount: number;
  reasoningCount: number;
  stageLabel: string;
  stageDetail: string | null;
  assistantText: string;
  assistantRenderMode: "plain" | "markdown";
  artifacts: TurnArtifact[];
};

export type HiddenTurnPreview = {
  turnId: string;
  createdAt: string;
  question: string;
  answer: string;
};

const STALE_RUNNING_TURN_MS = 2 * 60 * 1000;
const MAX_REASONABLE_STRONG_SPAN_CHARS = 72;
const MAX_STRONG_SPANS_PER_LINE = 2;

const repairMarkdownStructure = (text: string) =>
  text
    .replace(/\r\n/g, "\n")
    .replace(/([^\n])(\d+\.\s+(?=(\*\*|__|[A-Za-z(])))/g, "$1\n$2")
    .replace(/([^\n])(-\s+(?=(\*\*|__|[A-Za-z(])))/g, "$1\n$2")
    .split("\n")
    .map((line) => {
      const trimmed = line.trim();
      if (/^#{1,6}\s+/.test(trimmed) && (/\d+\.\s/.test(trimmed) || /\|/.test(trimmed))) {
        return trimmed.replace(/^#{1,6}\s+/, "");
      }
      return line;
    })
    .join("\n");

const stripDenseInlineStrong = (text: string) =>
  text
    .split("\n")
    .map((line) => {
      const strongSpanCount = (line.match(/(\*\*|__)([\s\S]+?)\1/g) ?? []).length;
      return strongSpanCount > MAX_STRONG_SPANS_PER_LINE ? line.replace(/\*\*|__/g, "") : line;
    })
    .join("\n");

const stripRunawayStrongSpans = (text: string) =>
  text.replace(/(\*\*|__)([\s\S]+?)\1/g, (match, _marker, content: string) => {
    const trimmed = content.trim();
    const looksLikeWholeSentence =
      /[.!?]/.test(trimmed) ||
      /\d+\./.test(trimmed) ||
      trimmed.includes("\n") ||
      trimmed.length > MAX_REASONABLE_STRONG_SPAN_CHARS;

    return looksLikeWholeSentence ? trimmed : match;
  });

const normalizeAssistantMarkdown = (text: string) => {
  let normalized = repairMarkdownStructure(text);

  normalized = stripRunawayStrongSpans(normalized);
  normalized = stripDenseInlineStrong(normalized);

  normalized = normalized
    .split("\n")
    .map((line) => {
      const doubleAsteriskCount = (line.match(/\*\*/g) ?? []).length;
      const normalizedAsterisks = doubleAsteriskCount % 2 !== 0 ? line.replace(/\*\*/g, "") : line;

      const doubleUnderscoreCount = (normalizedAsterisks.match(/__/g) ?? []).length;
      return doubleUnderscoreCount % 2 !== 0 ? normalizedAsterisks.replace(/__/g, "") : normalizedAsterisks;
    })
    .join("\n");

  return normalized.trim();
};

const parsePayload = (payload: string): Record<string, unknown> => {
  try {
    return JSON.parse(payload) as Record<string, unknown>;
  } catch {
    return {};
  }
};

const hasToolResultError = (resultSummary: Record<string, unknown> | undefined, payload: Record<string, unknown>) => {
  const status = typeof resultSummary?.status === "string" ? resultSummary.status.toLowerCase() : "";
  return Boolean(resultSummary?.error) || status === "error" || status === "failed" || Boolean(payload.error);
};

const isTurnArtifact = (value: unknown): value is TurnArtifact => {
  if (!value || typeof value !== "object") {
    return false;
  }
  const artifact = value as Record<string, unknown>;
  if (artifact.type === "chart") {
    return (
      typeof artifact.title === "string" &&
      typeof artifact.chart_type === "string" &&
      typeof artifact.dataset_name === "string" &&
      typeof artifact.x_key === "string" &&
      typeof artifact.y_key === "string" &&
      Array.isArray(artifact.data)
    );
  }
  if (artifact.type === "csv") {
    return (
      typeof artifact.title === "string" &&
      typeof artifact.file_name === "string" &&
      typeof artifact.dataset_name === "string" &&
      Array.isArray(artifact.columns) &&
      Array.isArray(artifact.rows)
    );
  }
  if (artifact.type === "table") {
    return (
      typeof artifact.title === "string" &&
      typeof artifact.artifact_id === "string" &&
      typeof artifact.dataset_name === "string" &&
      Array.isArray(artifact.columns) &&
      Array.isArray(artifact.rows)
    );
  }
  return false;
};

const formatJson = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "object" && Object.keys(value as Record<string, unknown>).length === 0) {
    return null;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const appendDetails = (existing: string | null | undefined, label: string, value: unknown) => {
  const formatted = formatJson(value);
  if (!formatted) {
    return existing ?? null;
  }

  if (!existing) {
    return `${label}\n${formatted}`;
  }

  return `${existing}\n\n${label}\n${formatted}`;
};

const isLegacyFakeTurn = (turn: TurnSummary) => {
  if (turn.activityEvents.some((event) => parsePayload(event.payload).mode === "backend-fake-runtime")) {
    return true;
  }
  return turn.assistantMessage?.text.includes("fake Nucleus runtime stream") ?? false;
};

export const mergeEvent = (current: ChatEvent[], incoming: ChatEvent) => {
  const next = [...current];
  const existingIndex = next.findIndex((event) => event.eventId === incoming.eventId);

  if (existingIndex >= 0) {
    next[existingIndex] = incoming;
  } else {
    next.push(incoming);
  }

  next.sort((a, b) => a.sequence - b.sequence);
  return next;
};

export const mergeEvents = (current: ChatEvent[], incoming: ChatEvent[]) => {
  let next = current;
  for (const event of incoming) {
    next = mergeEvent(next, event);
  }
  return next;
};

export const buildTurnSummaries = (events: ChatEvent[]): TurnSummary[] => {
  const byTurn = new Map<string, TurnSummary>();
  const assistantMessageIdByTurn = new Map<string, string>();

  for (const event of events) {
    const existing = byTurn.get(event.turnId) ?? {
      turnId: event.turnId,
      createdAt: event.createdAt,
      userMessage: null,
      assistantMessage: null,
      activityEvents: [],
      isLegacyFake: false,
    };
    const payload = parsePayload(event.payload);

    if (event.eventType === "USER_MESSAGE") {
      existing.userMessage = {
        id: event.eventId,
        text: String(payload.text ?? ""),
        createdAt: event.createdAt,
      };
    } else if (event.eventType === "TEXT_MESSAGE_START") {
      const messageId = String(payload.messageId ?? event.eventId);
      assistantMessageIdByTurn.set(event.turnId, messageId);
      existing.assistantMessage = {
        id: messageId,
        text: "",
        createdAt: event.createdAt,
      };
    } else if (event.eventType === "TEXT_MESSAGE_CONTENT" || event.eventType === "TEXT_MESSAGE_DELTA") {
      if (!existing.assistantMessage) {
        const messageId = assistantMessageIdByTurn.get(event.turnId) ?? String(payload.messageId ?? event.eventId);
        existing.assistantMessage = {
          id: messageId,
          text: "",
          createdAt: event.createdAt,
        };
      }

      const delta = String(payload.delta ?? "");
      existing.assistantMessage.text = `${existing.assistantMessage.text}${delta}`;
    }

    existing.activityEvents.push(event);
    byTurn.set(event.turnId, existing);
  }

  return [...byTurn.values()]
    .map((turn) => ({
      ...turn,
      isLegacyFake: isLegacyFakeTurn(turn),
    }))
    .sort((left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime());
};

export const limitVisibleTurns = (turns: TurnSummary[], maxVisibleTurns: number) => {
  if (turns.length <= maxVisibleTurns) {
    return { hiddenTurns: [] as TurnSummary[], visibleTurns: turns };
  }

  return {
    hiddenTurns: turns.slice(0, turns.length - maxVisibleTurns),
    visibleTurns: turns.slice(-maxVisibleTurns),
  };
};

const buildThinkingBlocks = (events: ChatEvent[]): ThinkingBlock[] => {
  const byMessageId = new Map<string, ThinkingBlock>();

  for (const event of events) {
    if (event.eventType !== "REASONING_MESSAGE_CONTENT") {
      continue;
    }

    const payload = parsePayload(event.payload);
    const messageId = String(payload.messageId ?? event.eventId);
    const delta = String(payload.delta ?? "");
    const existing = byMessageId.get(messageId) ?? { id: messageId, text: "" };
    existing.text += delta;
    byMessageId.set(messageId, existing);
  }

  return [...byMessageId.values()]
    .map((block) => ({ ...block, text: block.text.trim() }))
    .filter((block) => block.text.length > 0);
};

const getLatestEventTimestampMs = (events: ChatEvent[]) =>
  events.reduce((latest, event) => {
    const timestamp = Date.parse(event.createdAt);
    return Number.isNaN(timestamp) ? latest : Math.max(latest, timestamp);
  }, 0);

const getTurnSummaryAnswer = (events: ChatEvent[]) => {
  const summaryEvent = [...events].reverse().find((event) => event.eventType === "TURN_SUMMARY");
  if (!summaryEvent) {
    return "";
  }

  return String(parsePayload(summaryEvent.payload).answer ?? "");
};

const chooseCompleteAssistantText = (streamedText: string, summaryAnswer: string) => {
  if (!summaryAnswer) {
    return streamedText;
  }

  if (summaryAnswer.includes("[truncated]") && streamedText.length > summaryAnswer.length) {
    return streamedText;
  }

  return summaryAnswer;
};

const buildToolTraces = (events: ChatEvent[]): ToolTrace[] => {
  const traces = new Map<string, ToolTrace>();
  const order: string[] = [];

  const getToolTrace = (payload: Record<string, unknown>, fallbackId: string) => {
    const traceId = String(payload.toolCallId ?? payload.toolName ?? fallbackId);
    const existing = traces.get(traceId);
    if (existing) {
      return existing;
    }

    const created: ToolTrace = {
      id: traceId,
      title: String(payload.displayLabel ?? payload.toolName ?? "Tool call"),
      inputDetails: null,
      outputDetails: null,
      state: "working",
      artifact: null,
    };
    traces.set(traceId, created);
    order.push(traceId);
    return created;
  };

  for (const event of events) {
    if (!["TOOL_CALL_START", "TOOL_CALL_ARGS", "TOOL_CALL_END", "TOOL_CALL_RESULT"].includes(event.eventType)) {
      continue;
    }

    const payload = parsePayload(event.payload);
    const trace = getToolTrace(payload, event.eventId);

    if (event.eventType === "TOOL_CALL_ARGS") {
      trace.inputDetails = appendDetails(trace.inputDetails, "Input", payload.argsSummary ?? payload.delta);
      continue;
    }

    if (event.eventType === "TOOL_CALL_END" || event.eventType === "TOOL_CALL_RESULT") {
      trace.inputDetails = appendDetails(trace.inputDetails, "Input", payload.argsSummary);
      trace.outputDetails = appendDetails(
        trace.outputDetails,
        "Result",
        payload.resultSummary ?? payload.content ?? payload.delta
      );
      const resultSummary = payload.resultSummary as Record<string, unknown> | undefined;
      const artifact = resultSummary?.artifact;
      if (isTurnArtifact(artifact)) {
        trace.artifact = artifact;
      }
      trace.state = hasToolResultError(resultSummary, payload) ? "error" : "complete";
      continue;
    }

    trace.state = "working";
  }

  return order.map((traceId) => traces.get(traceId)).filter((trace): trace is ToolTrace => Boolean(trace));
};

export const buildTurnViewModel = (turn: TurnSummary): TurnViewModel => {
  const thinkingBlocks = buildThinkingBlocks(turn.activityEvents);
  const toolTraces = buildToolTraces(turn.activityEvents);
  const artifacts = toolTraces
    .map((toolTrace) => toolTrace.artifact)
    .filter((artifact): artifact is TurnArtifact => Boolean(artifact));
  const hasError = turn.activityEvents.some((event) => event.eventType === "RUN_ERROR");
  const isFinished = turn.activityEvents.some((event) => event.eventType === "RUN_FINISHED") || hasError;
  const streamedAssistantText = turn.assistantMessage?.text ?? "";
  const assistantText =
    isFinished && !hasError
      ? chooseCompleteAssistantText(streamedAssistantText, getTurnSummaryAnswer(turn.activityEvents))
      : streamedAssistantText;
  const normalizedAssistantText = normalizeAssistantMarkdown(assistantText);
  const latestEventTimestampMs = getLatestEventTimestampMs(turn.activityEvents);
  const isStale = !isFinished && latestEventTimestampMs > 0 && Date.now() - latestEventTimestampMs > STALE_RUNNING_TURN_MS;
  const toolCount = toolTraces.length;
  const reasoningCount = thinkingBlocks.length;

  if (hasError) {
    const errorEvent = [...turn.activityEvents].reverse().find((event) => event.eventType === "RUN_ERROR");
    const errorPayload = errorEvent ? parsePayload(errorEvent.payload) : {};
    return {
      turn,
      thinkingBlocks,
      toolTraces,
      isRunning: false,
      isStale: false,
      hasError: true,
      statusLabel: String(errorPayload.message ?? "This turn failed"),
      statusBadgeLabel: "Needs attention",
      statusTone: "error",
      toolCount,
      reasoningCount,
      stageLabel: "Run failed",
      stageDetail: String(errorPayload.message ?? "") || null,
      assistantText: normalizedAssistantText,
      assistantRenderMode: "plain",
      artifacts,
    };
  }

  if (isStale) {
    return {
      turn,
      thinkingBlocks,
      toolTraces,
      isRunning: false,
      isStale: true,
      hasError: false,
      statusLabel: "This earlier attempt stopped updating.",
      statusBadgeLabel: "Stopped",
      statusTone: "info",
      toolCount,
      reasoningCount,
      stageLabel: "Earlier attempt did not finish",
      stageDetail: "This turn is no longer active. Ask again to retry.",
      assistantText,
      assistantRenderMode: "plain",
      artifacts,
    };
  }

  if (!isFinished) {
    return {
      turn,
      thinkingBlocks,
      toolTraces,
      isRunning: true,
      isStale: false,
      hasError: false,
      statusLabel: "Working",
      statusBadgeLabel: "Working",
      statusTone: "working",
      toolCount,
      reasoningCount,
      stageLabel: "Working",
      stageDetail: null,
      assistantText,
      assistantRenderMode: "plain",
      artifacts,
    };
  }

  const completedLabel =
    toolCount > 0 ? `Answer ready · used ${toolCount} tool${toolCount === 1 ? "" : "s"}` : "Answer ready";

  return {
    turn,
    thinkingBlocks,
    toolTraces,
    isRunning: false,
    isStale: false,
    hasError: false,
    statusLabel: completedLabel,
    statusBadgeLabel: "Complete",
    statusTone: "complete",
    toolCount,
    reasoningCount,
    stageLabel: "Answer ready",
    stageDetail: null,
    assistantText: normalizedAssistantText,
    assistantRenderMode: "markdown",
    artifacts,
  };
};

export const buildHiddenTurnPreview = (turn: TurnSummary): HiddenTurnPreview => {
  const question = turn.userMessage?.text?.trim() || "Earlier turn in this conversation";
  const answer = buildTurnViewModel(turn).assistantText.trim();

  return {
    turnId: turn.turnId,
    createdAt: turn.createdAt,
    question,
    answer,
  };
};
